import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";

// P1.1 — This is a lazy-loaded settings panel, not a realtime attendance listener.
// Server callables enforce ownership, site scope, and delegated manager permission.
export default function PhotoAttendanceSettings({ orgId, stores = [] }) {
  const [storeId, setStoreId] = useState("");
  const [settings, setSettings] = useState(null);
  const [photoRequired, setPhotoRequired] = useState(false);
  const [clockOutPhotoRequired, setClockOutPhotoRequired] = useState(false);
  const [managerPermission, setManagerPermission] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionReindexing, setRetentionReindexing] = useState(false);
  const [retentionReindexNeeded, setRetentionReindexNeeded] = useState(false);
  const [retentionProgress, setRetentionProgress] = useState("");
  const [confirmReindexOpen, setConfirmReindexOpen] = useState(false);
  const confirmationDialogRef = useRef(null);
  const confirmationCancelRef = useRef(null);
  const confirmationTriggerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!stores.some((store) => store.id === storeId)) {
      setStoreId(stores[0]?.id || "");
    }
  }, [stores, storeId]);

  useEffect(() => {
    if (!orgId || !storeId) return undefined;
    let cancelled = false;
    setSettings(null);
    setConfirmReindexOpen(false);
    setRetentionProgress("");
    setRetentionReindexNeeded(false);
    setLoading(true);
    setError("");
    setNotice("");
    const call = httpsCallable(functions, "neoPhotoClockInSettings");
    call({ orgId, storeId, action: "read" })
      .then(({ data }) => {
        if (cancelled) return;
        setSettings(data);
        setPhotoRequired(data?.photoRequired === true);
        setClockOutPhotoRequired(data?.clockOutPhotoRequired === true);
        setManagerPermission(data?.managersMayChangePhotoRequirement === true);
        setRetentionDays(Number(data?.photoRetentionDays) || 30);
        setRetentionReindexNeeded(data?.retentionReindexNeeded === true);
      })
      .catch((err) => { if (!cancelled) setError(err?.message || "Could not load photo attendance settings."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, storeId]);

  const save = async () => {
    if (!settings || saving || !settings.canChangePhotoRequirement) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const call = httpsCallable(functions, "neoPhotoClockInSettings");
      const payload = { orgId, storeId, action: "save", photoRequired, clockOutPhotoRequired };
      // Managers cannot modify the delegation flag, even accidentally.
      if (settings.canDelegate) payload.managersMayChangePhotoRequirement = managerPermission;
      const { data } = await call(payload);
      // The photo-requirement response does not have to overwrite an independently
      // configured retention period in the same panel.
      setSettings((previous) => ({ ...previous, ...data }));
      setPhotoRequired(data.photoRequired === true);
      setClockOutPhotoRequired(data.clockOutPhotoRequired === true);
      setManagerPermission(data.managersMayChangePhotoRequirement === true);
      setNotice("Photo attendance configuration saved.");
    } catch (err) {
      setError(err?.message || "Could not save configuration.");
    } finally {
      setSaving(false);
    }
  };

  const saveRetention = async () => {
    if (!settings?.canDelegate || retentionSaving || retentionReindexing) return;
    setRetentionSaving(true); setError(""); setNotice("");
    try {
      const { data } = await httpsCallable(functions, "neoPhotoClockInSettings")({
        orgId, storeId, action: "saveRetention", retentionDays,
      });
      setRetentionDays(Number(data.photoRetentionDays) || retentionDays);
      setRetentionReindexNeeded(true);
      setRetentionProgress("Saved. Apply to existing photographs if this policy should cover earlier attendance records.");
      setNotice(`Photo retention set to ${data.photoRetentionDays} days for this site.`);
    } catch (err) {
      setError(err?.message || "Could not save photo retention.");
    } finally { setRetentionSaving(false); }
  };

  // The native browser confirm() has been replaced with a branded, accessible
  // confirmation dialog. Backend actions remain exactly as before.
  const requestReindexConfirmation = () => {
    if (!settings?.canDelegate || retentionReindexing || retentionSaving || !retentionReindexNeeded) return;
    confirmationTriggerRef.current = document.activeElement;
    setConfirmReindexOpen(true);
  };

  const cancelReindexConfirmation = () => setConfirmReindexOpen(false);

  useEffect(() => {
    if (!confirmReindexOpen || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    confirmationCancelRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        cancelReindexConfirmation();
      } else if (event.key === "Tab") {
        const controls = Array.from(confirmationDialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []);
        if (!controls.length) { event.preventDefault(); return; }
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !confirmationDialogRef.current?.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !confirmationDialogRef.current?.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      confirmationTriggerRef.current?.focus();
    };
  }, [confirmReindexOpen]);

  const reindexExisting = async () => {
    if (!settings?.canDelegate || retentionReindexing || !retentionReindexNeeded || !confirmReindexOpen) return;
    setConfirmReindexOpen(false);
    setRetentionReindexing(true); setError(""); setNotice("");
    let cursor = ""; let checked = 0; let updated = 0;
    try {
      // One-time, bounded calls. No realtime listeners and no recurring full scans.
      for (let page = 0; page < 1000; page += 1) {
        const { data } = await httpsCallable(functions, "neoPhotoClockInSettings")({
          orgId, storeId, action: "reindexRetention", ...(cursor ? { cursor } : {}),
        });
        checked += Number(data.scanned || 0);
        updated += Number(data.updated || 0);
        cursor = data.nextCursor || "";
        setRetentionProgress(`Examined ${checked} attendance records; updated ${updated}.`);
        if (!cursor) {
          setRetentionReindexNeeded(false);
          setNotice(`Retention applied to existing photos. ${updated} attendance records updated.`);
          break;
        }
        if (page === 999) throw new Error("More attendance history remains. Contact MatrixOS support to resume safely.");
      }
    } catch (err) {
      setError(err?.message || "Existing photographs could not all be updated.");
      setRetentionProgress(`Partial progress: ${checked} records examined. Contact MatrixOS to resume from the saved page if needed.`);
    } finally { setRetentionReindexing(false); }
  };

  return (
    <div className="space-y-4 rounded-lg border border-white/10 bg-white/5 p-4">
      <div className="text-xs text-amber-200/90">
Photo capture and retention require the current Neo App, Firebase Functions, indexes and security rules. Expired photos are removed by the daily cleanup.
      </div>
      <div>
        <label htmlFor="neo-photo-site" className="mb-1 block text-sm font-medium">Site</label>
        <select id="neo-photo-site" className="w-full max-w-md rounded-lg border border-white/20 bg-slate-900 p-2 text-white" value={storeId} disabled={retentionReindexing || retentionSaving} onChange={(e) => setStoreId(e.target.value)}>
          {stores.length ? stores.map((s) => <option key={s.id} value={s.id}>{s.name || s.storeName || s.id}</option>) : <option value="">No active sites</option>}
        </select>
      </div>
      {loading ? <p className="text-sm text-white/60">Loading selected site's settings…</p> : null}
      {settings && !loading ? (
        <>
          <label className="flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={photoRequired} onChange={(e) => { setPhotoRequired(e.target.checked); setNotice(""); }} disabled={!settings.canChangePhotoRequirement || saving} />
            <span>
              <span className="block font-semibold">Require PIN + photo on clock-in</span>
              <span className="block text-sm text-white/60">Applies only to this site. Other branches are unaffected.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 border-t border-white/10 pt-3">
            <input type="checkbox" className="mt-1" checked={clockOutPhotoRequired} onChange={(e) => { setClockOutPhotoRequired(e.target.checked); setNotice(""); }} disabled={!settings.canChangePhotoRequirement || saving} />
            <span>
              <span className="block font-semibold">Require PIN + photo on clock-out</span>
              <span className="block text-sm text-white/60">Separate from clock-in. Applies to manual clock-outs at this site; automated clock-outs are not photo-verified.</span>
            </span>
          </label>
          {settings.canDelegate ? (
            <label className="flex items-start gap-3 border-t border-white/10 pt-3">
              <input type="checkbox" className="mt-1" checked={managerPermission} onChange={(e) => { setManagerPermission(e.target.checked); setNotice(""); }} disabled={saving} />
              <span>
                <span className="block font-semibold">Allow authorised Neo Admin managers to change this setting</span>
                <span className="block text-sm text-white/60">Off by default. Only the Owner can grant this permission. Managers must also have Neo Admin Settings access and permission for this site. This does not give access to attendance photos.</span>
              </span>
            </label>
          ) : (
            <p className="text-sm text-white/60">
              {settings.canChangePhotoRequirement
                ? "The Owner has permitted you to change photo attendance requirements for this site. You cannot grant this permission to others."
                : "Only the Owner can change this requirement unless permission is delegated."}
            </p>
          )}
          {settings.canChangePhotoRequirement ? (
            <button type="button" onClick={save} disabled={saving} className="neo-btn neo-btn-accent disabled:opacity-50">{saving ? "Saving…" : "Save photo attendance settings"}</button>
          ) : null}
          <section className="mt-5 space-y-3 border-t border-white/10 pt-4" aria-label="Photo retention">
            <div>
              <h4 className="font-semibold">Photo retention &amp; automatic deletion</h4>
              <p className="mt-1 text-sm text-white/60">Choose how long Neo keeps clock-in and clock-out photographs at this site. Attendance times, hours and payroll history are retained. Expired photographs are deleted during the daily cleanup, not immediately when you save.</p>
            </div>
            <div>
              <label htmlFor="neo-photo-retention-days" className="mb-1 block text-sm font-medium">Keep photographs for</label>
              <select id="neo-photo-retention-days" value={retentionDays} onChange={(e) => { setRetentionDays(Number(e.target.value)); setRetentionReindexNeeded(false); setRetentionProgress("Save the new period before updating existing photographs."); setNotice(""); }}
                disabled={!settings.canDelegate || retentionSaving || retentionReindexing}
                className="w-full max-w-md rounded-lg border border-white/20 bg-slate-900 p-2 text-white">
                {[7, 14, 30, 60, 90, 180, 365].map((days) => <option key={days} value={days}>{days} days{days === 30 ? " (default)" : ""}</option>)}
              </select>
            </div>
            {settings.canDelegate ? (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={saveRetention} disabled={retentionSaving || retentionReindexing} className="neo-btn neo-btn-accent disabled:opacity-50">
                  {retentionSaving ? "Saving…" : "Save retention period"}
                </button>
                {retentionReindexNeeded && !retentionSaving ? (
                  <button type="button" onClick={requestReindexConfirmation} disabled={retentionReindexing || retentionSaving} className="neo-btn disabled:opacity-50">
                    {retentionReindexing ? "Updating existing photos…" : "Apply to existing photos"}
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-white/60">Only the organisation Owner can change photo retention.</p>
            )}
            {retentionProgress ? <p role="status" className="text-sm text-sky-200">{retentionProgress}</p> : null}
            <p className="text-xs text-amber-200/90">Before shortening retention, confirm statutory record-keeping obligations and whether any photographs must be preserved for an investigation or legal hold. Deletion also removes owner access to those images.</p>
          </section>
        </>
      ) : null}
      {error ? <p role="alert" className="text-sm text-rose-300">{error}</p> : null}
      {notice ? <p role="status" className="text-sm text-emerald-300">{notice}</p> : null}

      {confirmReindexOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto bg-black/75 px-4 py-6"
          onMouseDown={(event) => { if (event.target === event.currentTarget) cancelReindexConfirmation(); }}
        >
          <div
            ref={confirmationDialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="neo-retention-confirm-title"
            aria-describedby="neo-retention-confirm-description"
            className="w-full max-w-lg rounded-2xl border border-white/20 bg-slate-900 p-5 text-white shadow-2xl sm:p-6"
          >
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-xl text-amber-200">!</span>
              <div className="min-w-0 flex-1">
                <h3 id="neo-retention-confirm-title" className="text-lg font-bold">Apply retention to existing photos?</h3>
                <p id="neo-retention-confirm-description" className="mt-2 text-sm leading-relaxed text-white/75">
                  Apply the saved <strong className="text-white">{retentionDays}-day</strong> retention period to existing clock-in and clock-out photos at <strong className="text-white">{stores.find((site) => site.id === storeId)?.name || stores.find((site) => site.id === storeId)?.storeName || storeId}</strong>?
                </p>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-amber-300/25 bg-amber-400/10 p-3 text-sm leading-relaxed text-amber-100">
              Older photos could become eligible for permanent deletion during the next daily cleanup. This cannot be undone from Neo. Photos needed for an investigation or legal hold should be preserved first.
            </div>
            <p className="mt-3 text-sm text-white/65">Attendance dates, recorded hours and payroll history will remain.</p>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                ref={confirmationCancelRef}
                type="button"
                onClick={cancelReindexConfirmation}
                className="rounded-lg border border-white/25 bg-white/5 px-4 py-2.5 font-semibold text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={reindexExisting}
                className="rounded-lg border border-amber-500/50 bg-amber-500 px-4 py-2.5 font-semibold text-slate-950 hover:bg-amber-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300"
              >
                Apply to existing photos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
